import { useEffect, useId, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: string | HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback': () => void;
          'error-callback': () => void;
          theme: 'dark';
          size: 'flexible';
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

type TurnstileProps = {
  siteKey: string;
  onToken: (token: string | null) => void;
};

let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-agam-turnstile]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Turnstile no disponible')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.agamTurnstile = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Turnstile no disponible'));
    document.head.append(script);
  });
  return scriptPromise;
}

export function Turnstile({ siteKey, onToken }: TurnstileProps) {
  const reactId = useId();
  const containerId = `turnstile-${reactId.replaceAll(':', '')}`;
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    let active = true;
    void loadTurnstile().then(() => {
      if (!active || !window.turnstile) return;
      widgetId.current = window.turnstile.render(`#${containerId}`, {
        sitekey: siteKey,
        theme: 'dark',
        size: 'flexible',
        callback: (token) => onToken(token),
        'expired-callback': () => onToken(null),
        'error-callback': () => onToken(null),
      });
    });

    return () => {
      active = false;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      onToken(null);
    };
  }, [containerId, onToken, siteKey]);

  return <div id={containerId} className="min-h-[65px] w-full" aria-label="Verificación humana" />;
}
